import React, { useState, useEffect } from 'react';
import { BarChart3, Filter, Wallet, MousePointer, Target, TrendingUp, Calendar } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface AdsManagerContentProps {
  animationDelay?: number;
}

const AdsManagerContent: React.FC<AdsManagerContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [stats, setStats] = useState({ impressions: 0, clicks: 0, conversions: 0, cpc: 0, spend: 0 });
  const [showCharts, setShowCharts] = useState([false, false, false]);
  const [chartHeights, setChartHeights] = useState([0, 0, 0, 0, 0, 0, 0]);

  useEffect(() => {
    // Animate stats counting up
    const duration = 1500;
    const steps = 30;
    let step = 0;
    
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setStats({
        impressions: Math.round(24650 * progress),
        clicks: Math.round(1842 * progress),
        conversions: Math.round(156 * progress),
        cpc: Number((0.84 * progress).toFixed(2)),
        spend: Math.round(1548 * progress)
      });
      if (step >= steps) clearInterval(timer);
    }, duration / steps);

    // Animate charts appearing
    setTimeout(() => setShowCharts([true, false, false]), animationDelay + 800);
    setTimeout(() => setShowCharts([true, true, false]), animationDelay + 1000);
    setTimeout(() => setShowCharts([true, true, true]), animationDelay + 1200);

    // Animate chart bars
    setTimeout(() => {
      setChartHeights([30, 45, 60, 40, 75, 55, 80]);
    }, animationDelay + 1000);

    return () => clearInterval(timer);
  }, [animationDelay]);

  const summaryCards = [
    { 
      icon: Filter, 
      iconBg: '#FEF3C7', 
      iconColor: '#F59E0B',
      label: 'Impressions', 
      value: stats.impressions.toLocaleString(),
      subItems: [
        { label: 'Clicks', value: stats.clicks.toLocaleString() },
        { label: 'Conversions', value: stats.conversions.toString() }
      ]
    },
    { 
      icon: Target, 
      iconBg: '#DBEAFE', 
      iconColor: '#3B82F6',
      label: 'Average CPC', 
      value: `£${stats.cpc}`,
      subItems: [
        { label: 'Cost per Conversion', value: '£9.92' },
        { label: 'Cost per Mille (CPM)', value: '£6.28' }
      ]
    },
    { 
      icon: Wallet, 
      iconBg: '#D1FAE5', 
      iconColor: '#10B981',
      label: 'Client Spends', 
      value: `£${stats.spend.toLocaleString()}`,
      subItems: []
    }
  ];

  return (
    <div 
      className="flex-1 p-2 lg:p-4 overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#222222' : '#F9F9F9',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Header */}
      <div 
        className="mb-2 lg:mb-4"
        style={{
          animation: `fadeIn 0.4s ease-out ${animationDelay + 100}ms both`
        }}
      >
        <h3 
          className="text-[10px] lg:text-sm font-bold mb-1 lg:mb-2"
          style={{ color: isDark ? '#FFFFFF' : '#111111' }}
        >
          Ad Manager
        </h3>
        
        {/* Tabs - simplified on mobile */}
        <div className="hidden lg:flex gap-4 border-b mb-3" style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}>
          <span 
            className="text-[10px] pb-2"
            style={{ color: isDark ? '#888888' : '#666666' }}
          >
            Campaigns
          </span>
          <span 
            className="text-[10px] pb-2 border-b-2"
            style={{ color: '#3B82F6', borderColor: '#3B82F6' }}
          >
            Statistics
          </span>
        </div>

        {/* Filters - hidden on mobile */}
        <div 
          className="hidden lg:flex gap-2 mb-4"
          style={{
            animation: `fadeIn 0.4s ease-out ${animationDelay + 200}ms both`
          }}
        >
          <div 
            className="flex items-center gap-1.5 px-2 py-1 rounded text-[9px]"
            style={{ 
              backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
              border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`
            }}
          >
            <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
              <span className="text-white text-[8px] font-bold">M</span>
            </div>
            <span style={{ color: isDark ? '#FFFFFF' : '#111111' }}>Meta</span>
          </div>
          <div 
            className="flex items-center gap-1.5 px-2 py-1 rounded text-[9px]"
            style={{ 
              backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
              border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`
            }}
          >
            <Calendar className="w-3 h-3" style={{ color: isDark ? '#888888' : '#666666' }} />
            <span style={{ color: isDark ? '#888888' : '#666666' }}>Last 30 days</span>
          </div>
        </div>
      </div>

      {/* Conversion Summary */}
      <div 
        className="mb-2 lg:mb-4"
        style={{
          animation: `fadeIn 0.4s ease-out ${animationDelay + 300}ms both`
        }}
      >
        <span 
          className="text-[8px] lg:text-[10px] font-semibold mb-1 lg:mb-2 block"
          style={{ color: isDark ? '#FFFFFF' : '#111111' }}
        >
          Conversion Summary
        </span>
        
        <div className="grid grid-cols-3 gap-1 lg:gap-3">
          {summaryCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div 
                key={index}
                className="p-1.5 lg:p-3 rounded-lg overflow-hidden"
                style={{ 
                  backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
                  border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
                  animation: `scaleIn 0.4s ease-out ${animationDelay + 400 + (index * 100)}ms both`
                }}
              >
                <div className="flex items-start gap-1 lg:gap-2 mb-1 lg:mb-2">
                  <div 
                    className="w-4 h-4 lg:w-6 lg:h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: card.iconBg }}
                  >
                    <Icon className="w-2 h-2 lg:w-3 lg:h-3" style={{ color: card.iconColor }} />
                  </div>
                  <div className="min-w-0">
                    <span 
                      className="text-[7px] lg:text-[9px] block truncate"
                      style={{ color: card.iconColor }}
                    >
                      {card.label}
                    </span>
                    <span 
                      className="text-[10px] lg:text-lg font-bold block truncate"
                      style={{ color: isDark ? '#FFFFFF' : '#111111' }}
                    >
                      {card.value}
                    </span>
                  </div>
                </div>
                {card.subItems.length > 0 && (
                  <div className="hidden lg:block space-y-1 pt-2 border-t" style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}>
                    {card.subItems.map((sub, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-[9px] truncate" style={{ color: isDark ? '#888888' : '#666666' }}>{sub.label}</span>
                        <span className="text-[10px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{sub.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Performance Analytics */}
      <div 
        style={{
          animation: `fadeIn 0.4s ease-out ${animationDelay + 600}ms both`
        }}
      >
        <span 
          className="text-[8px] lg:text-[10px] font-semibold mb-1 lg:mb-2 block"
          style={{ color: isDark ? '#FFFFFF' : '#111111' }}
        >
          Analytics
        </span>
        
        <div className="grid grid-cols-3 gap-1 lg:gap-3">
          {[
            { label: 'Impressions', value: stats.impressions.toLocaleString() },
            { label: 'Clicks', value: stats.clicks.toLocaleString() },
            { label: 'Conv.', value: stats.conversions.toString() }
          ].map((chart, index) => (
            <div 
              key={index}
              className="p-1.5 lg:p-3 rounded-lg overflow-hidden"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
                border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
                opacity: showCharts[index] ? 1 : 0,
                transform: showCharts[index] ? 'translateY(0)' : 'translateY(10px)',
                transition: 'all 0.4s ease-out'
              }}
            >
              <span className="text-[7px] lg:text-[9px] truncate block" style={{ color: '#3B82F6' }}>{chart.label}</span>
              <div 
                className="text-[10px] lg:text-sm font-bold mb-1 lg:mb-2 truncate"
                style={{ color: isDark ? '#FFFFFF' : '#111111' }}
              >
                {chart.value}
              </div>
              
              {/* Mini Chart */}
              <div className="flex items-end gap-0.5 h-4 lg:h-8">
                {chartHeights.map((height, i) => (
                  <div 
                    key={i}
                    className="flex-1 rounded-t"
                    style={{ 
                      backgroundColor: '#3B82F6',
                      height: `${height}%`,
                      opacity: 0.7 + (i * 0.05),
                      transition: `height 0.5s ease-out ${i * 50}ms`
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default AdsManagerContent;
